import { inject, Injectable, InjectionToken } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import type { SavePipelineInput } from '@protocols/use-cases';

import { AssignedPipelinesEffects } from '../../../effects/assigned-pipelines.effects';
import { PipelineEffects } from '../../../effects/pipeline.effects';
import { PipelineEditorEffects } from '../../../effects/pipeline-editor.effects';
import { AssignedPipelinesState } from '../../../state/assigned-pipelines.state';
import type { MethodPipelineContext } from '../../../state/assigned-pipelines.state';
import { PipelineData, PipelineState } from '../../../state/pipeline.state';
import {
  StagedPipelineDraft,
  StagedPipelineOverride,
  StagedPipelinesState,
} from '../../../state/staged-pipelines.state';
import { PipelineEditorState } from '../../../state/pipeline-editor.state';
import { PipelineListItem } from '../pipelines-list.component';
import { concatLatestFrom } from '@ngrx/operators';

/**
 * The method type this manager assigns pipelines to. Provided per mount (the
 * method-type id + whether it is already persisted).
 * A staged (not-yet-persisted) method type has
 * no `ProtocolPipeline` rows to fetch, so its assigned list is staged-only.
 */
export const METHOD_PIPELINE_MANAGER_CONTEXT = new InjectionToken<MethodPipelineContext>(
  'METHOD_PIPELINE_MANAGER_CONTEXT'
);

@Injectable()
export class MethodPipelineManagerFacade {
  private readonly context = inject<MethodPipelineContext>(METHOD_PIPELINE_MANAGER_CONTEXT);

  constructor(
    private readonly pipelineEffects: PipelineEffects,
    private readonly pipelineState: PipelineState,
    private readonly stagedPipelines: StagedPipelinesState,
    private readonly assignedEffects: AssignedPipelinesEffects,
    private readonly assignedState: AssignedPipelinesState,
    private readonly pipelineEditorEffects: PipelineEditorEffects,
    private readonly pipelineEditorState: PipelineEditorState
  ) {}

  // ============================================================================
  // Left list — global pipeline catalog, with the per-row `assigned` flag
  // derived from the right list's assigned set (workspace-members pattern).
  // ============================================================================

  public readonly availablePipelines$: Observable<PipelineListItem[]> = combineLatest([
    this.pipelineEffects.pipelines$,
    this.assignedEffects.assignedIds$,
  ]).pipe(
    map(([items, assignedIds]) => items.map((item: PipelineData) => ({ ...item, assigned: assignedIds.has(item.id) })))
  );

  /** Current app language — drives the list components' multilingual name display. */
  public readonly currentLanguage$ = this.pipelineEffects.currentLanguage$;

  public readonly searchTerm$ = this.pipelineEffects.searchTerm$;
  public readonly totalCount$ = this.pipelineEffects.totalCount$;
  public readonly pageSize$ = this.pipelineEffects.pageSize$;
  public readonly nextPipelineToken$ = this.pipelineEffects.nextToken$;
  public readonly previousPipelineTokens$ = this.pipelineEffects.previousTokens$;
  public readonly noPipelines$ = this.pipelineEffects.noItems$;
  public readonly counting$ = this.pipelineEffects.counting$;
  public readonly loading$ = combineLatest([
    this.pipelineEffects.loading$,
    this.pipelineEffects.searching$,
    this.pipelineEffects.counting$,
  ]).pipe(map(([loading, searching, counting]) => loading || searching || counting));

  // ============================================================================
  // Right list — pipelines assigned to the method type (paginated, no search).
  // Every row is assigned, so the toggle un-assigns.
  // ============================================================================

  public readonly assignedPipelines$: Observable<PipelineListItem[]> = this.assignedEffects.items$.pipe(
    map((items) => items.map((item: PipelineData) => ({ ...item, assigned: true })))
  );

  public readonly assignedNoPipelines$ = this.assignedEffects.noItems$;
  public readonly assignedTotalCount$ = this.assignedEffects.totalCount$;
  public readonly assignedPageSize$ = this.assignedEffects.pageSize$;
  public readonly assignedNextToken$ = this.assignedEffects.nextToken$;
  public readonly assignedPreviousTokens$ = this.assignedEffects.previousTokens$;
  public readonly assignedCounting$ = this.assignedEffects.counting$;
  public readonly assignedLoading$ = combineLatest([
    this.assignedEffects.loading$,
    this.assignedEffects.counting$,
  ]).pipe(map(([loading, counting]) => loading || counting));

  // ============================================================================
  // Commands
  // ============================================================================

  /** Load the assigned set (right) and the catalog (left). */
  public readonly initialize = this.pipelineState.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => {
        this.assignedEffects.initialize(this.context);
        this.pipelineEffects.initializePipelines();
      })
    )
  );

  /** Catalog search (alias of the list state's updater; triggers the reload). */
  public readonly search = this.pipelineEffects.setSearchTerm;

  /** Catalog pagination. */
  public readonly loadMore = this.pipelineEffects.loadMorePipelines;

  /** Assigned-list pagination. */
  public readonly loadMoreAssigned = this.assignedEffects.loadMoreAssigned;

  /** Toggle a left-list (catalog) row: un-assign if assigned, assign otherwise. */
  public readonly toggleAvailable = this.assignedState.effect((item$: Observable<PipelineListItem>) =>
    item$.pipe(
      tap((item: PipelineListItem) => {
        if (item.assigned) {
          this.assignedEffects.unassign(item.id);
        } else {
          this.assignedEffects.assign(this.toPipelineData(item));
        }
      })
    )
  );

  /** Un-assign a right-list row. */
  public readonly unassign = this.assignedState.effect((item$: Observable<PipelineListItem>) =>
    item$.pipe(tap((item: PipelineListItem) => this.assignedEffects.unassign(item.id)))
  );

  // ============================================================================
  // Editor-dialog choreography
  // ============================================================================

  /** Original of the pipeline currently being edited */
  private readonly _editOriginal$ = new BehaviorSubject<PipelineData | null>(null);

  /** Seed the editor for a brand-new pipeline before opening the dialog. */
  public readonly beginCreatePipeline = this.pipelineEditorState.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => {
        this._editOriginal$.next(null);
        this.pipelineEditorEffects.loadPipeline(null);
      })
    )
  );

  /**
   * Seed the editor for an existing pipeline (captures its pre-edit data). If
   * the pipeline already has a staged draft from this session (a created
   * pipeline, or one edited earlier), seed from that draft so the user sees
   * their staged edits; otherwise load the persisted detail from the DB.
   */
  public readonly beginEditPipeline = this.pipelineEditorState.effect((item$: Observable<PipelineListItem>) =>
    item$.pipe(
      concatLatestFrom(() => [this.stagedPipelines.pendingPipelines$, this.stagedPipelines.pipelineOverrides$]),
      tap(
        ([item, pendingPipelines, pipelineOverrides]: [
          PipelineListItem,
          StagedPipelineDraft[],
          Record<string, StagedPipelineOverride>
        ]) => {
          this._editOriginal$.next(this.toPipelineData(item));
          const stagedDraft = pendingPipelines.find((d) => d.id === item.id) ?? pipelineOverrides[item.id]?.draft;
          if (stagedDraft) {
            this.pipelineEditorEffects.seedFromDraft(stagedDraft);
          } else {
            this.pipelineEditorEffects.loadPipeline(item.id);
          }
        }
      )
    )
  );

  /**
   * Apply the editor's produced draft on `saved`. The full draft is always
   * retained (so a re-open shows the staged edits and the eventual cascade can
   * persist them); the list-display projection depends on the kind of edit:
   *
   * - brand-new pipeline (no id) → client temp id, pending catalog row,
   *   auto-assigned to the method type;
   * - re-edit of a still-pending pipeline → the pending row is upserted in
   *   place (NOT turned into an override, which would duplicate it);
   * - edit of a persisted pipeline → a catalog override is recorded and the
   *   assigned representation is patched.
   */
  public readonly stageSavedPipeline = this.pipelineState.effect<void>((trigger$) =>
    trigger$.pipe(
      concatLatestFrom(() => [
        this.pipelineEditorState.draft$,
        this._editOriginal$,
        this.stagedPipelines.pendingPipelines$,
      ]),
      tap(
        ([, draft, original, pendingPipelines]: [
          void,
          SavePipelineInput,
          PipelineData | null,
          StagedPipelineDraft[]
        ]) => this.applySavedDraft(draft, original, pendingPipelines)
      )
    )
  );

  // ============================================================================
  // Helpers
  // ============================================================================

  private applySavedDraft(
    draft: SavePipelineInput,
    original: PipelineData | null,
    pendingPipelines: StagedPipelineDraft[]
  ): void {
    const numSteps = draft.steps?.length ?? 0;

    if (!draft.id) {
      // Brand-new pipeline: client temp id, pending in the catalog, auto-assigned.
      const id = `tmp-${uuidv4()}`;
      const pipeline: PipelineData = { id, name: draft.name, env: draft.env, numSteps, createdAt: new Date() };
      this.stagedPipelines.stagePending({ ...draft, id });
      this.assignedEffects.assign(pipeline);
    } else if (this.isPending(draft.id, pendingPipelines)) {
      // Re-edit of a not-yet-persisted pipeline: update the pending entry in
      // place, keeping the date it was staged with.
      const pipeline: PipelineData = {
        id: draft.id,
        name: draft.name,
        env: draft.env,
        numSteps,
        createdAt: original?.createdAt ?? new Date(),
      };
      this.stagedPipelines.stagePending(draft);
      this.assignedEffects.applyEdit(pipeline);
    } else {
      // Edit of a persisted pipeline: catalog override + patch the assigned row.
      const override: PipelineData = {
        id: draft.id,
        name: draft.name,
        env: draft.env,
        numSteps,
        createdAt: original?.createdAt ?? new Date(),
      };
      const base: PipelineData = original ?? {
        id: draft.id,
        name: draft.name,
        env: draft.env,
        numSteps,
        createdAt: new Date(),
      };
      this.stagedPipelines.stageOverride({ original: base, draft });
      this.assignedEffects.applyEdit(override);
    }

    // Reflect the staged catalog change (pending row / override) in the visible page.
    this.pipelineEffects.relistPage();
    this.pipelineEffects.countItems();
    this._editOriginal$.next(null);
  }

  /** Whether `id` belongs to a not-yet-persisted (pending) catalog pipeline. */
  private isPending(id: string, pendingPipelines: StagedPipelineDraft[]): boolean {
    return pendingPipelines.some((p) => p.id === id);
  }

  private toPipelineData(item: PipelineListItem): PipelineData {
    return {
      id: item.id,
      name: item.name,
      env: item.env,
      createdAt: item.createdAt,
      usedByMethodTypes: item.usedByMethodTypes,
      numSteps: item.numSteps,
    };
  }
}
